/**
 * The record page's vocabulary — the one shared by every module.
 *
 * A record is a thing with a page of its own: a contact, a property, a job, a
 * visit, an invoice, a piece of equipment. Activities and files hang off
 * records rather than off modules, which is the whole point of the pattern —
 * a note written on a visit is the same row as a note written on a contact,
 * and the next module to want notes needs no tables.
 *
 * This file is shared because the API and the UI have to agree on the words:
 * the server decides which link column a `property` means, the client decides
 * which icon it wears, and a typo in either is a compile error rather than a
 * blank tab.
 */
import { z } from 'zod';
import { taskCategoryIdsSchema, taskPrioritySchema } from './activity/task-planner.js';
/** Everything that can have activities and files hung off it. */
export const RECORD_TYPES = [
    'contact', 'property', 'job', 'visit', 'invoice', 'equipment',
    /*
     * The Visit page's record (`docs/visit-page.md`, lead decision 17): a work
     * item — on screen *Visit*, at `/visits/:id` — for its summary, Activity,
     * Files and History. `visit` keeps meaning a day, so old activity links,
     * file links and mentions still resolve.
     */
    'work_item',
    /*
     * A quote, for its Activity tab (`docs/walkthrough-findings-2026-10-30.md`
     * §4, folder 52): the shared activity component, as the job and the
     * contact have it. Never a lookup or attach choice — a quote is opened from
     * its job (`LOOKUP_KINDS`, `AttachType` leave it out).
     */
    'quote',
];
export const recordTypeSchema = z.enum(RECORD_TYPES);
/**
 * The records Rowted Admin has — a customer, and one of our own admins.
 *
 * A second list rather than two more entries in `RECORD_TYPES`, because that
 * one is "things a tenant can write a note on" and these are neither in a
 * tenant nor note-able: `activities` and `files` are tenant tables with a
 * `tenant_id`, and a client is the row that *owns* tenants. What the two lists
 * share is the record **page** — the shell, the pills, the tabs — so
 * `AnyRecordType` is what that shell is keyed by and nothing else.
 */
export const PLATFORM_RECORD_TYPES = ['client', 'admin'];
export const platformRecordTypeSchema = z.enum(PLATFORM_RECORD_TYPES);
/** A reference to one record, which is how everything here is addressed. */
export const recordRefSchema = z.object({
    type: recordTypeSchema,
    id: z.string().uuid(),
});
// ---------------------------------------------------------------------------
// Activities
// ---------------------------------------------------------------------------
/**
 * The six things a person can write down about a record.
 *
 * `note` is what was there before under another name; the other five are the
 * ones a field business actually does — somebody rang, somebody has to ring,
 * somebody is coming on Tuesday. A text message is `sms` in the database and
 * "Text" on the screen, because that is what people call it.
 */
export const ACTIVITY_KINDS = ['note', 'task', 'call', 'email', 'sms', 'meeting'];
export const ACTIVITY_KIND_LABELS = {
    note: 'Note',
    task: 'Task',
    call: 'Call',
    email: 'Email',
    sms: 'Text',
    meeting: 'Meeting',
};
/** What the composer's button says once a kind is chosen. */
export const ACTIVITY_KIND_VERBS = {
    note: 'Add note',
    task: 'Add task',
    call: 'Log call',
    email: 'Log email',
    sms: 'Log text',
    meeting: 'Log meeting',
};
/**
 * Every kind a stored row may be: the composer's six, `appointment`, a
 * salesperson's site visit made by New request (`docs/new-request.md` §4.1,
 * migration 0132), and `block`, Activity's time block (0134). Kept apart from `ACTIVITY_KINDS` on purpose — that list is
 * what the record composer offers, and no record gains an Appointment chip.
 * Read and validate stored rows against this one.
 */
export const STORED_ACTIVITY_KINDS = [...ACTIVITY_KINDS, 'appointment', 'block'];
export const STORED_ACTIVITY_KIND_LABELS = {
    ...ACTIVITY_KIND_LABELS,
    appointment: 'Site visit',
    // A person's time, never a record's (`docs/activity.md` §4.1, Aaron point 7).
    block: 'Time block',
};
/** The one kind of appointment there is, for now (`activities.appointment_type`). */
export const APPOINTMENT_TYPES = ['site_visit'];
/**
 * How an appointment ended (`activities.outcome`, free text for other kinds).
 * Cancelling also sets `done_at`, so a cancelled one leaves Next up and
 * Upcoming — and the row stays (rule 1).
 */
export const APPOINTMENT_OUTCOMES = ['done', 'no_show', 'cancelled'];
/**
 * Kinds that are about something that has not happened yet, and so carry a
 * date, an assignee and a tick box. The others are a record of something that
 * already did.
 */
export const SCHEDULED_KINDS = ['task', 'meeting'];
/**
 * Who can see it. There is no customer-facing value on purpose: a note is an
 * internal record, and the thing a customer is allowed to see is a file with
 * `portal` visibility.
 */
export const ACTIVITY_VISIBILITIES = ['internal', 'crew'];
/** The filter chips over the timeline, and what each one keeps. */
export const ACTIVITY_FILTERS = [
    'all', 'notes', 'tasks', 'comms', 'work', 'invoices', 'system',
];
export const ACTIVITY_FILTER_LABELS = {
    all: 'All',
    notes: 'Notes',
    tasks: 'Tasks',
    comms: 'Calls & messages',
    work: 'Jobs & visits',
    invoices: 'Invoices',
    system: 'System',
};
/** Where a meeting is (§9.6): a property, an address, the branch office, or by phone or video. */
export const ACTIVITY_LOCATION_KINDS = ['property', 'address', 'office', 'remote'];
export const activityAddressSchema = z.object({
    line1: z.string().trim().max(200),
    line2: z.string().trim().max(200).optional(),
    city: z.string().trim().max(100).optional(),
    state: z.string().trim().max(40).optional(),
    postalCode: z.string().trim().max(20).optional(),
    latitude: z.number().min(-90).max(90).optional(),
    longitude: z.number().min(-180).max(180).optional(),
});
export const activityInputSchema = z.object({
    kind: z.enum(ACTIVITY_KINDS),
    title: z.string().trim().max(200).optional(),
    body: z.string().trim().max(20_000).optional(),
    dueAt: z.string().datetime().nullish(),
    assigneeId: z.string().uuid().nullish(),
    reminderMinutes: z.number().int().min(0).max(60 * 24 * 30).nullish(),
    visibility: z.enum(ACTIVITY_VISIBILITIES).default('internal'),
    durationMinutes: z.number().int().min(0).max(60 * 24).nullish(),
    outcome: z.string().trim().max(200).nullish(),
    /** Every record this is about. The first one is where it was written. */
    links: z.array(recordRefSchema).min(1).max(10),
    /** Files already uploaded, attached to this activity as it is created. */
    fileIds: z.array(z.string().uuid()).max(20).optional(),
    /*
     * Activity (`docs/activity.md` §4, §9, §15). All optional, so a record
     * composer that sends none of them writes exactly what it always did.
     */
    /** Put back what a job or contact brings along (F2). The New sheets send `false`. */
    expand: z.boolean().optional(),
    /**
     * Who it is for, first first (§4.7). Not capped here: a 26th person is the
     * service's 422 with the sentence the sheet prints, not a schema's 400.
     */
    participantIds: z.array(z.string().uuid()).max(200).optional(),
    locationKind: z.enum(ACTIVITY_LOCATION_KINDS).nullish(),
    locationPropertyId: z.string().uuid().nullish(),
    locationAddress: activityAddressSchema.nullish(),
    repeatRule: z.enum(['daily', 'weekly', 'every_other_week', 'monthly', 'yearly']).nullish(),
    repeatUntil: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullish(),
    /** The rest of how it repeats — Outlook's options (migration 0138, `recurrence.ts`). */
    repeatInterval: z.number().int().min(1).max(99).optional(),
    repeatWeekdays: z.array(z.number().int().min(0).max(6)).min(1).max(7).nullish(),
    repeatMonthNth: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4), z.literal(-1)]).nullish(),
    repeatCount: z.number().int().min(1).max(999).nullish(),
    inputMode: z.enum(['typed', 'voice']).optional(),
    madeFromActivityId: z.string().uuid().nullish(),
    madeFromCommunicationId: z.string().uuid().nullish(),
    /** The phone's id for a queued note (§11.4): the same id again is the same row. */
    clientId: z.string().uuid().nullish(),
    taskListId: z.string().uuid().nullish(),
    bucketId: z.string().uuid().nullish(),
    /** False for a task due on a day with no time (§8.2): it stays off the calendar. */
    dueHasTime: z.boolean().optional(),
    /**
     * When a logged call was placed (`docs/activity.md` §10.3: *You called Renee
     * Castillo at 9:44 AM*), so the job's Activity shows it then rather than
     * when it was saved. A call only, and only within the 30 minutes *Log the
     * call?* is offered; anything else is stamped when it is written.
     */
    occurredAt: z.string().datetime().nullish(),
    /** A task's priority and categories, set as it is made (Aaron, Oct 25, note 1.5; 0144). */
    priority: taskPrioritySchema.optional(),
    categoryIds: taskCategoryIdsSchema.optional(),
    /**
     * A meeting's *With* cleared while a contact stays attached (Aaron, Oct 26,
     * answer 10; 0150): with nobody from outside. Absent or false, *With* is its
     * first contact, as it always was.
     */
    withNobody: z.boolean().optional(),
});
// ---------------------------------------------------------------------------
// Files
// ---------------------------------------------------------------------------
/**
 * What a file *is* to the business, rather than what shape its bytes are.
 *
 * A closed set, because the point of it is that a crew looking for the site
 * plan and an office looking for the signed contract are both looking for a
 * kind — and free text gives you "Contract", "contract" and "CONTRACT FINAL
 * v2" as three different kinds of the same thing.
 */
export const FILE_TYPES = [
    'photo', 'video', 'audio', 'contract', 'site_plan', 'permit', 'insurance', 'other',
];
export const FILE_TYPE_LABELS = {
    photo: 'Photo',
    /* A video taken on site and a voice note's recording (activity.md §4.2, §11). */
    video: 'Video',
    audio: 'Audio',
    contract: 'Contract',
    site_plan: 'Site plan',
    permit: 'Permit',
    insurance: 'Insurance',
    other: 'Other',
};
export const FILE_VISIBILITIES = ['internal', 'crew', 'portal'];
export const FILE_VISIBILITY_LABELS = {
    internal: 'Internal',
    crew: 'Crew',
    portal: 'Customer portal',
};
/** An expiry inside this many days is worth saying out loud, in red. */
export const FILE_EXPIRY_WARNING_DAYS = 120;
/**
 * What a file's type should be before anybody is asked.
 *
 * Nobody should have to classify a photo, and a PDF whose name says
 * "agreement" is a contract often enough to be worth guessing at — the
 * uploader can change it in the same dialog, which is cheaper than making
 * them choose every time.
 */
export function guessFileType(name, contentType) {
    if (contentType.startsWith('image/'))
        return 'photo';
    if (contentType.startsWith('video/'))
        return 'video';
    if (contentType.startsWith('audio/'))
        return 'audio';
    if (/agreement|contract|msa|proposal signed/i.test(name))
        return 'contract';
    if (/site.?plan|irrigation|zones|sketch|layout/i.test(name))
        return 'site_plan';
    if (/permit/i.test(name))
        return 'permit';
    if (/insurance|coi|certificate/i.test(name))
        return 'insurance';
    return 'other';
}
export const fileAttributesSchema = z.object({
    type: z.enum(FILE_TYPES).optional(),
    visibility: z.enum(FILE_VISIBILITIES).optional(),
    expiresOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullish(),
    caption: z.string().trim().max(500).optional(),
    originalName: z.string().trim().min(1).max(260).optional(),
});
//# sourceMappingURL=records.js.map